import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Label,
  Toastify,
  SpinnerLoader,
  DataTable,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { Edit } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import apiClient from "@lift-admin/utils/apiClient";
import { logAdminAction } from "@lift-admin/utils/actionLogTrack";
import { diffFields, type FieldSpec } from "@lift-admin/utils/logChangeFormatter";

const BRAND_PARAM_SPECS: FieldSpec[] = [
  { key: "productDescription", label: "Product Description" },
  { key: "valueProps", label: "Value Props" },
  { key: "targetAudience", label: "Target Audience" },
  { key: "dos", label: "Do's" },
  { key: "donts", label: "Don'ts" },
  { key: "additionalInfo", label: "Additional Info" },
];

// Brand params are long free-text fields; truncate for readable log entries.
const truncateForLog = (value?: string, max = 160): string | undefined => {
  if (typeof value !== "string") return value;
  return value.length > max ? `${value.slice(0, max)}…` : value;
};

interface Brand {
  id: number | string;
  brand_name?: string;
  name?: string;
  [key: string]: any;
}

interface FormData {
  productDescription: string;
  valueProps: string;
  targetAudience: string;
  dos: string;
  donts: string;
  additionalInfo: string;
}

const defaultFormData: FormData = {
  productDescription: "",
  valueProps: "",
  targetAudience: "",
  dos: "",
  donts: "",
  additionalInfo: "",
};

interface BrandTableProps {
  searchQuery: string;
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

const BrandTable: React.FC<BrandTableProps> = ({ searchQuery, hasEditAccess = true, hasManageAccess = true }) => {
  const [allBrands, setAllBrands] = useState<Brand[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [originalFormData, setOriginalFormData] =
    useState<FormData>(defaultFormData);

  // Check if there are changes in the form
  const hasChanges = useMemo(() => {
    return (
      formData.productDescription !== originalFormData.productDescription ||
      formData.valueProps !== originalFormData.valueProps ||
      formData.targetAudience !== originalFormData.targetAudience ||
      formData.dos !== originalFormData.dos ||
      formData.donts !== originalFormData.donts ||
      formData.additionalInfo !== originalFormData.additionalInfo
    );
  }, [formData, originalFormData]);

  // Define getBrandDisplayName function before using it in useMemo
  const getBrandDisplayName = (brand: Brand): string => {
    return brand.brand_name || brand.name || `Brand ${brand.id}`;
  };

  // Filter brands based on search query
  const filteredBrands = useMemo(() => {
    if (!searchQuery.trim()) {
      return allBrands;
    }

    const lowerCaseQuery = searchQuery.toLowerCase();
    return allBrands.filter((brand) => {
      const brandName = getBrandDisplayName(brand).toLowerCase();
      return brandName.includes(lowerCaseQuery);
    });
  }, [allBrands, searchQuery]);

  // Calculate pagination
  const paginatedBrands = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredBrands.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredBrands, page, rowsPerPage]);

  useEffect(() => {
    fetchAllBrands();
  }, []);

  // Reset to first page when search query changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const fetchAllBrands = async () => {
    setLoading(true);
    try {
      // Get all brands from the admin brands endpoint
      const response = await apiClient.get(`/brand/v1/admin/brands/all`);

      if (response.data) {
        setAllBrands(response.data.response || []);
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      Toastify("error", "Failed to fetch brands");
    } finally {
      setLoading(false);
    }
  };


  const handleOpenDialog = async (brand: Brand) => {
    setSelectedBrand(brand);
    await getBrandInfo(brand.id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedBrand(null);
    setFormData(defaultFormData);
    setOriginalFormData(defaultFormData);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      handleCloseDialog();
    }
  };

  const getBrandInfo = async (brandId: number | string) => {
    if (!brandId) {
      return;
    }
    try {
      const response = await apiClient.get(`/mr-oven/api/brand/${brandId}`);
      if (response.data.metadata) {
        const brandData = response.data.metadata.brand_oven_meta_data;
        const mappedFormData: FormData = {
          productDescription: brandData.productDescription || "",
          valueProps: brandData.valueProps || "",
          targetAudience: brandData.targetAudience || "",
          dos: brandData.dos || "",
          donts: brandData.donts || "",
          additionalInfo: brandData.additionalInfo || "",
        };
        setFormData(mappedFormData);
        setOriginalFormData(mappedFormData);
      } else {
        setFormData(defaultFormData);
        setOriginalFormData(defaultFormData);
      }
    } catch (error) {
      console.error("Error fetching brand info:", error);
      Toastify("warning", "No Brand Information Found");
      setFormData(defaultFormData);
      setOriginalFormData(defaultFormData);
    }
  };

  const updateBrandInfo = async () => {
    if (!selectedBrand) {
      Toastify("error", "Please select a brand first");
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await apiClient.post(`/mr-oven/api/brand/metadata`, {
        user_id: user.id,
        brand_id: selectedBrand.id,
        metadata: formData,
      });
      Toastify("success", "Brand info updated successfully");
      const changes = diffFields(originalFormData, formData, BRAND_PARAM_SPECS).map(c => ({
        ...c,
        from: truncateForLog(c.from),
        to: truncateForLog(c.to),
      }));
      await logAdminAction(
        `Brand parameters updated for "${getBrandDisplayName(selectedBrand)}"${
          changes.length ? ` (${changes.map(c => c.label).join(', ')})` : ''
        }`,
        {
          url: `/mr-oven/api/brand/metadata`,
          method: 'POST',
          status: 200,
          category: 'brand',
          action: 'update_brand_params',
          brandId: selectedBrand.id,
          brandName: getBrandDisplayName(selectedBrand),
          changes,
        }
      );
      // Update the original form data to match the current data after successful save
      setOriginalFormData({ ...formData });
      handleCloseDialog();
    } catch (error: any) {
      console.error("Error updating brand info:", error);
      Toastify("error", "Failed to update brand information");
      await logAdminAction(
        `Failed to update brand parameters for "${getBrandDisplayName(selectedBrand)}": ${error?.response?.data?.detail || error?.message || 'Unknown error'}`,
        { url: `/mr-oven/api/brand/metadata`, method: 'POST', category: 'brand', action: 'update_brand_params', brandId: selectedBrand.id },
        true
      );
    }
  };

  const handleChange =
    (field: string) =>
      (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setFormData({ ...formData, [field]: e.target.value });
      };

  const totalPages = Math.max(1, Math.ceil(filteredBrands.length / rowsPerPage));

  const dataTableColumns = useMemo((): TableColumn<Brand>[] => {
    const cols: TableColumn<Brand>[] = [
      {
        key: "brand_name",
        header: "Brand Name",
        accessor: (row) => getBrandDisplayName(row),
        width: "80%",
        render: (_, row) => getBrandDisplayName(row),
      },
    ];
    if (hasEditAccess) {
      cols.push({
        key: "actions",
        header: "Actions",
        accessor: () => "",
        align: "center",
        render: (_, row) => (
          <div className="pr-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleOpenDialog(row)}
              className="h-8 w-8 p-0"
            >
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        ),
      });
    }
    return cols;
  }, [hasEditAccess]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 min-h-0">
        <DataTable<Brand>
          className="flex-1 min-h-0 overflow-auto"
          maxHeight="calc(100vh - 200px)"
          columns={dataTableColumns}
          data={paginatedBrands}
          loading={loading}
          emptyMessage={searchQuery ? "No brands found matching your search" : "No brands available"}
          stickyHeader
          stickyFooter
          showActions={false}
          pagination={{
            currentPage: page + 1,
            totalPages,
            pageSize: rowsPerPage,
            totalItems: filteredBrands.length,
            onPageChange: (p) => setPage(p - 1),
            onPageSizeChange: (size) => {
              setRowsPerPage(size);
              setPage(0);
            },
            pageSizeOptions: [10, 25, 50, 100],
          }}
        />
      </div>

      {/* Edit Sheet */}
      <Sheet open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-5xl flex flex-col">
          <SheetHeader className="flex-shrink-0">
            <SheetTitle>
              Edit Brand Parameters for{" "}
              {selectedBrand ? getBrandDisplayName(selectedBrand) : ""}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="productDescription" className="text-sm font-medium">
                  Product Description
                </Label>
                <Textarea
                  id="productDescription"
                  rows={3}
                  value={formData.productDescription}
                  onChange={handleChange("productDescription")}
                  placeholder="Enter product description"
                  className="resize-y min-h-[60px]"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="valueProps" className="text-sm font-medium">
                  Value Propositions
                </Label>
                <Textarea
                  id="valueProps"
                  rows={3}
                  value={formData.valueProps}
                  onChange={handleChange("valueProps")}
                  placeholder="Enter value propositions"
                  className="resize-y min-h-[60px]"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="targetAudience" className="text-sm font-medium">
                  Target Audience
                </Label>
                <Textarea
                  id="targetAudience"
                  rows={3}
                  value={formData.targetAudience}
                  onChange={handleChange("targetAudience")}
                  placeholder="Enter target audience information"
                  className="resize-y min-h-[60px]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Do's and Don'ts</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="dos" className="text-sm font-medium text-gray-700">
                      Do's
                    </Label>
                    <Textarea
                      id="dos"
                      rows={3}
                      value={formData.dos}
                      onChange={handleChange("dos")}
                      placeholder="No do's provided"
                      className="resize-y min-h-[60px]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="donts" className="text-sm font-medium text-gray-700">
                      Don'ts
                    </Label>
                    <Textarea
                      id="donts"
                      rows={3}
                      value={formData.donts}
                      onChange={handleChange("donts")}
                      placeholder="No don'ts provided"
                      className="resize-y min-h-[60px]"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="additionalInfo" className="text-sm font-medium">
                  Additional Information
                </Label>
                <Textarea
                  id="additionalInfo"
                  rows={3}
                  value={formData.additionalInfo}
                  onChange={handleChange("additionalInfo")}
                  placeholder="Enter additional information"
                  className="resize-y min-h-[60px]"
                />
              </div>
            </div>
          </div>
          <SheetFooter className="border-t pt-3 mt-3 flex-shrink-0">
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            {hasEditAccess && (
              <Button
                onClick={updateBrandInfo}
                disabled={!hasChanges}
                variant="default"
              >
                Save Changes
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default BrandTable;
